import { useEffect, useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { abrirArquivo } from "../lib/open-file.js";
import { Paginacao } from "../components/common/Paginacao.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";

const PAGE_SIZE = 50;

// Categorias contábeis francesas oficiais (SIG / PRD). Mantidas em FR para
// alinhar com relatórios fiscais para o cabinet Cockpit.
const CATEGORY_LABELS = {
  achats_consommes: "Achats consommés",
  fournitures_consommables: "Fournitures & consommables",
  locations_charges_locatives: "Locations & charges locatives",
  primes_assurance: "Primes d'assurance",
  intermediaires_honoraires: "Intermédiaires & honoraires",
  deplacements_reception: "Déplacements & réception",
  frais_bancaires: "Frais bancaires",
  impots_taxes: "Impôts & taxes",
  charges_personnel: "Charges de personnel",
  autres_charges_exploitation: "Autres charges d'exploitation",
  autres: "Outros",
} as const satisfies Record<string, string>;

const STATUS_TONE: Record<string, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  received: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};

type Category = keyof typeof CATEGORY_LABELS;

interface PiForm {
  supplierId: number | null;
  invoiceNumberExternal: string;
  category: Category;
  totalHt: string;
  tvaRate: string;
  currency: string;
  issueDate: string;
  dueDate: string;
  notes: string;
}

const emptyForm: PiForm = {
  supplierId: null,
  invoiceNumberExternal: "",
  category: "autres",
  totalHt: "0",
  tvaRate: "20",
  currency: "EUR",
  issueDate: new Date().toISOString().slice(0, 10),
  dueDate: "",
  notes: "",
};

export default function PurchaseInvoices() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const t = useT();
  const statusLabels: Record<string, string> = {
    draft: t("purchaseInvoices.statusDraftBadge"),
    received: t("purchaseInvoices.statusReceivedBadge"),
    paid: t("purchaseInvoices.statusPaidBadge"),
    cancelled: t("purchaseInvoices.statusCancelledBadge"),
  };
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [supplierFilter, setSupplierFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      supplierId: supplierFilter ?? undefined,
      status:
        statusFilter === "all"
          ? undefined
          : (statusFilter as "draft" | "received" | "paid" | "cancelled"),
      // "Todas (exceto canceladas)" é o padrão: esconde o lixo de teste/erros sem
      // apagar nada. Selecionar "Cancelada" mostra-as.
      excludeCancelled: statusFilter === "all",
      q: q || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, supplierFilter, statusFilter, q, offset],
  );

  const listQuery = trpc.purchaseInvoices.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const suppliersQuery = trpc.suppliers.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const markReceivedMutation = trpc.purchaseInvoices.markReceived.useMutation({
    onSuccess: () => utils.purchaseInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("purchaseInvoices.error"),
        message: `${t("purchaseInvoices.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const markPaidMutation = trpc.purchaseInvoices.markPaid.useMutation({
    onSuccess: () => utils.purchaseInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("purchaseInvoices.error"),
        message: `${t("purchaseInvoices.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const cancelMutation = trpc.purchaseInvoices.cancel.useMutation({
    onSuccess: () => utils.purchaseInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("purchaseInvoices.error"),
        message: `${t("purchaseInvoices.error")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const [modal, setModal] = useState<{ mode: "create" } | null>(null);
  const [attachPiId, setAttachPiId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  /**
   * F-bulk-attach — anexa MUITOS PDFs de uma vez, casando cada arquivo com a
   * fatura de fornecedor de mesmo número externo (nome do arquivo sem extensão =
   * nº externo da fatura). Resolve os ids num lookup só e envia sequencialmente.
   */
  async function handleBulkAttach(fileList: FileList | null) {
    if (companyId == null || !fileList || fileList.length === 0) return;
    // Aceita seleção de arquivos OU de pasta inteira (webkitdirectory) — filtra só PDFs.
    const files = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (files.length === 0) return;
    const fileNum = (f: File) =>
      f.name
        .replace(/\.[^.]+$/, "")
        .replace(/\s*\(\d+\)\s*$/, "")
        .trim();
    const numbers = [...new Set(files.map(fileNum).filter(Boolean))];
    if (numbers.length === 0) return;

    setBulkBusy(true);
    setBulkMsg(t("purchaseInvoices.bulkMatching"));
    try {
      const matches = await utils.purchaseInvoices.resolveByNumbers.fetch({ companyId, numbers });
      const byNum = new Map(matches.map((m) => [m.number.toUpperCase(), m.id]));

      let ok = 0;
      const unmatched: string[] = [];
      const failed: string[] = [];
      let i = 0;
      for (const f of files) {
        i += 1;
        const id = byNum.get(fileNum(f).toUpperCase());
        if (id == null) {
          unmatched.push(f.name);
          continue;
        }
        setBulkMsg(`${t("purchaseInvoices.bulkSendingPre")} ${i}/${files.length}…`);
        try {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("companyId", String(companyId));
          fd.append("ownerId", String(id));
          fd.append("ownerKind", "purchase_invoice");
          const resp = await fetch("/uploads", {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          ok += 1;
        } catch {
          failed.push(f.name);
        }
      }
      await utils.purchaseInvoices.invalidate();
      await utils.attachments.countByOwners.invalidate();

      const sample = (arr: string[]) =>
        arr.slice(0, 8).join(", ") + (arr.length > 8 ? `, +${arr.length - 8}` : "");
      const lines = [`✅ ${ok} ${t("purchaseInvoices.bulkAttachedSuf")}`];
      if (unmatched.length)
        lines.push(
          `⚠️ ${unmatched.length} ${t("purchaseInvoices.bulkUnmatchedSuf")}: ${sample(unmatched)}`,
        );
      if (failed.length)
        lines.push(`❌ ${failed.length} ${t("purchaseInvoices.bulkFailedSuf")}: ${sample(failed)}`);
      void modals.alert({
        title: t("purchaseInvoices.bulkDoneTitle"),
        message: lines.join("\n\n"),
        tone: unmatched.length || failed.length ? "warning" : "default",
      });
    } catch (e) {
      void modals.alert({
        title: t("purchaseInvoices.bulkErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    } finally {
      setBulkBusy(false);
      setBulkMsg(null);
    }
  }

  // F-pi-attachment — contagem de anexos por fatura visível (1 query batch p/ o
  // badge 📎 sem disparar N requisições).
  const visiblePiIds = useMemo(
    () => (listQuery.data?.rows ?? []).map((r) => r.id),
    [listQuery.data],
  );
  const attachCountsQuery = trpc.attachments.countByOwners.useQuery(
    { companyId: companyId ?? 0, ownerKind: "purchase_invoice", ownerIds: visiblePiIds },
    { enabled: companyId !== null && visiblePiIds.length > 0 },
  );
  const attachCounts: Record<number, number> = attachCountsQuery.data ?? {};

  if (!user) return null;

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("purchaseInvoices.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;
  const suppliers = suppliersQuery.data?.rows ?? [];
  const supplierById = new Map(suppliers.map((s) => [s.id, s]));

  return (
    <Shell>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 grow basis-full sm:basis-0">
          <FilterPanel testId="purchaseInvoices-filter-panel">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <FilterField label={t("purchaseInvoices.filterCompany")}>
                <select
                  value={companyId ?? ""}
                  onChange={(e) => {
                    setCompanyId(Number(e.target.value));
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-56"}
                >
                  {accessibleCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("purchaseInvoices.filterSupplier")}>
                <select
                  value={supplierFilter ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSupplierFilter(v === "" ? null : Number(v));
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-56"}
                >
                  <option value="">{t("purchaseInvoices.allSuppliers")}</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("purchaseInvoices.filterStatus")}>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-64"}
                >
                  <option value="all">{t("purchaseInvoices.filterAllExceptCancelled")}</option>
                  <option value="draft">{t("purchaseInvoices.statusDraft")}</option>
                  <option value="received">{t("purchaseInvoices.statusReceived")}</option>
                  <option value="paid">{t("purchaseInvoices.statusPaid")}</option>
                  <option value="cancelled">{t("purchaseInvoices.statusCancelled")}</option>
                </select>
              </FilterField>
              <FilterField label={t("purchaseInvoices.filterSearch")} className="grow sm:max-w-xs">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setOffset(0);
                  }}
                  placeholder={t("purchaseInvoices.searchPlaceholder")}
                  className={FILTER_INPUT + " w-full"}
                />
              </FilterField>
            </div>
          </FilterPanel>
        </div>
        {canEdit && (
          <button
            onClick={() => setModal({ mode: "create" })}
            disabled={suppliers.length === 0}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
          >
            {t("purchaseInvoices.newBtn")}
          </button>
        )}
      </div>

      {/* F-bulk-attach — anexar muitos PDFs de uma vez, casando pelo nome do arquivo. */}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2">
          <span className="text-xs font-medium text-neutral-300">
            {t("purchaseInvoices.bulkAttachTitle")}
          </span>
          <label
            className={`cursor-pointer rounded border border-lime/50 bg-lime/10 px-3 py-1.5 text-sm text-lime hover:bg-lime/20 ${
              bulkBusy || companyId == null ? "pointer-events-none opacity-40" : ""
            }`}
            title={t("purchaseInvoices.bulkPickFilesTitle")}
          >
            {bulkBusy
              ? (bulkMsg ?? t("purchaseInvoices.sending"))
              : t("purchaseInvoices.bulkPickFiles")}
            <input
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              disabled={bulkBusy || companyId == null}
              onChange={(e) => {
                void handleBulkAttach(e.target.files);
                e.currentTarget.value = "";
              }}
            />
          </label>
          <span className="text-xs text-neutral-500">{t("purchaseInvoices.or")}</span>
          <label
            className={`cursor-pointer rounded border border-sky-500/50 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-300 hover:bg-sky-500/20 ${
              bulkBusy || companyId == null ? "pointer-events-none opacity-40" : ""
            }`}
            title={t("purchaseInvoices.bulkPickFolderTitle")}
          >
            {t("purchaseInvoices.bulkPickFolder")}
            <input
              type="file"
              className="hidden"
              disabled={bulkBusy || companyId == null}
              ref={(el) => {
                if (el) {
                  el.setAttribute("webkitdirectory", "");
                  el.setAttribute("directory", "");
                }
              }}
              onChange={(e) => {
                void handleBulkAttach(e.target.files);
                e.currentTarget.value = "";
              }}
            />
          </label>
          <span className="text-[11px] text-neutral-500">
            {t("purchaseInvoices.bulkHintPre")} <code>HERTZ-2025-0042.pdf</code>).{" "}
            <strong>{t("purchaseInvoices.bulkHintFolderQuoted")}</strong>{" "}
            {t("purchaseInvoices.bulkHintSuf")}
          </span>
        </div>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("purchaseInvoices.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("purchaseInvoices.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-28">{t("purchaseInvoices.colDate")}</th>
                <th className="text-left px-3 py-2">{t("purchaseInvoices.colSupplier")}</th>
                <th className="text-left px-3 py-2 w-36">
                  {t("purchaseInvoices.colExternalNumber")}
                </th>
                <th className="text-left px-3 py-2 w-44">{t("purchaseInvoices.colCategory")}</th>
                <th className="text-right px-3 py-2 w-28 font-mono">
                  {t("purchaseInvoices.colHt")}
                </th>
                <th className="text-right px-3 py-2 w-20">{t("purchaseInvoices.colTvaRate")}</th>
                <th className="text-right px-3 py-2 w-28 font-mono">
                  {t("purchaseInvoices.colTva")}
                </th>
                <th className="text-right px-3 py-2 w-28 font-mono">
                  {t("purchaseInvoices.colTtc")}
                </th>
                <th className="text-left px-3 py-2 w-24">{t("purchaseInvoices.colStatus")}</th>
                <th className="px-3 py-2 w-44 text-right">{t("purchaseInvoices.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((pi) => {
                const supplier = supplierById.get(pi.supplierId);
                return (
                  <tr key={pi.id}>
                    <td className="px-3 py-2 text-xs text-neutral-400">{pi.issueDate ?? "—"}</td>
                    <td className="px-3 py-2">{supplier?.displayName ?? `#${pi.supplierId}`}</td>
                    <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                      {pi.invoiceNumberExternal}
                    </td>
                    <td className="px-3 py-2 text-xs text-neutral-400">
                      {CATEGORY_LABELS[pi.category] ?? pi.category}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{formatMoney(pi.totalHt)}</td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-400">
                      {pi.tvaRate}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-400">
                      {formatMoney(pi.tvaAmount)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      {formatMoney(pi.totalTtc)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_TONE[pi.status]}`}
                      >
                        {statusLabels[pi.status] ?? pi.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => setAttachPiId(pi.id)}
                            title={t("purchaseInvoices.attachBtnTitle")}
                            className={`rounded-md border px-2 py-0.5 text-xs ${
                              (attachCounts[pi.id] ?? 0) > 0
                                ? "border-lime/40 text-lime hover:border-lime"
                                : "border-neutral-800 text-neutral-400 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-600"
                            }`}
                          >
                            📎{(attachCounts[pi.id] ?? 0) > 0 ? ` ${attachCounts[pi.id]}` : ""}
                          </button>
                        )}
                        {canEdit && pi.status === "draft" && (
                          <button
                            onClick={() =>
                              markReceivedMutation.mutate({
                                companyId: companyId!,
                                purchaseInvoiceId: pi.id,
                              })
                            }
                            disabled={markReceivedMutation.isPending}
                            className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-2 py-0.5 text-xs"
                          >
                            {t("purchaseInvoices.markReceivedBtn")}
                          </button>
                        )}
                        {canEdit && pi.status === "received" && (
                          <button
                            onClick={() =>
                              markPaidMutation.mutate({
                                companyId: companyId!,
                                purchaseInvoiceId: pi.id,
                              })
                            }
                            disabled={markPaidMutation.isPending}
                            className="rounded-md border border-lime/40 hover:border-lime text-lime px-2 py-0.5 text-xs"
                          >
                            {t("purchaseInvoices.markPaidBtn")}
                          </button>
                        )}
                        {canEdit && pi.status !== "cancelled" && isAdmin && (
                          <button
                            onClick={async () => {
                              const reason = await modals.promptReason({
                                title: `${t("purchaseInvoices.cancelInvoiceTitlePre")} ${pi.invoiceNumberExternal}`,
                                message: t("purchaseInvoices.cancelInvoiceMessage"),
                                placeholder: t("purchaseInvoices.cancelReasonPlaceholder"),
                                confirmLabel: t("purchaseInvoices.cancelInvoiceConfirm"),
                                tone: "danger",
                                required: true,
                              });
                              if (!reason) return;
                              cancelMutation.mutate({
                                companyId: companyId!,
                                purchaseInvoiceId: pi.id,
                                reason,
                              });
                            }}
                            disabled={cancelMutation.isPending}
                            className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs"
                          >
                            {t("purchaseInvoices.cancelBtn")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={
            total === 1
              ? t("purchaseInvoices.invoiceSingular")
              : t("purchaseInvoices.invoicePlural")
          }
          testId="purchaseInvoices-pagination"
        />
      )}

      {modal && companyId !== null && (
        <PiCreateModal
          companyId={companyId}
          suppliers={suppliers}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.purchaseInvoices.invalidate();
          }}
        />
      )}

      {attachPiId !== null &&
        companyId !== null &&
        (() => {
          const pi = data?.rows.find((r) => r.id === attachPiId);
          const header = pi
            ? `${supplierById.get(pi.supplierId)?.displayName ?? `#${pi.supplierId}`} · ${pi.invoiceNumberExternal} · ${formatMoney(pi.totalTtc)} TTC`
            : "";
          return (
            <PiAttachmentsModal
              companyId={companyId}
              purchaseInvoiceId={attachPiId}
              header={header}
              isAdmin={isAdmin}
              onClose={() => setAttachPiId(null)}
              onChanged={() => utils.attachments.countByOwners.invalidate()}
            />
          );
        })()}
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// F-pi-attachment — Modal: anexar/ver/remover PDF de uma fatura de fornecedor JÁ
// registrada (antes só dava ao criar). Usa ownerKind='purchase_invoice', então o
// anexo também alimenta o link da fatura no PDF do extrato e relatórios.
// ────────────────────────────────────────────────────────────────────────────

function PiAttachmentsModal({
  companyId,
  purchaseInvoiceId,
  header,
  isAdmin,
  onClose,
  onChanged,
}: {
  companyId: number;
  purchaseInvoiceId: number;
  header: string;
  isAdmin: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const modals = useModals();
  const utils = trpc.useUtils();
  const t = useT();
  const listQuery = trpc.attachments.listByOwner.useQuery({
    companyId,
    ownerKind: "purchase_invoice",
    ownerId: purchaseInvoiceId,
  });
  const deleteMut = trpc.attachments.delete.useMutation();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = listQuery.data ?? [];

  function refresh() {
    void listQuery.refetch();
    void utils.attachments.invalidate();
    onChanged();
  }

  async function handleUpload(fileList: FileList | null) {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("companyId", String(companyId));
        fd.append("ownerId", String(purchaseInvoiceId));
        fd.append("ownerKind", "purchase_invoice");
        const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? t("purchaseInvoices.uploadFailed"));
        }
      }
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function openAttachment(attachmentId: number) {
    try {
      await abrirArquivo(
        async () => (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId })).url,
      );
    } catch (e) {
      void modals.alert({
        title: t("purchaseInvoices.error"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  async function removeAttachment(attachmentId: number, filename: string) {
    const ok = await modals.confirm({
      title: t("purchaseInvoices.removeAttachmentTitle"),
      message: `${t("purchaseInvoices.removeAttachmentPre")} "${filename}"? ${t("purchaseInvoices.removeAttachmentSuf")}`,
      confirmLabel: t("purchaseInvoices.removeConfirm"),
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync({ companyId, attachmentId });
      refresh();
    } catch (e) {
      void modals.alert({
        title: t("purchaseInvoices.error"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  return (
    <ModalShell title={t("purchaseInvoices.attachmentsModalTitle")} onClose={onClose}>
      <div className="p-5 space-y-4">
        {header && (
          <div className="bg-neutral-800/30 rounded-md px-3 py-2 text-sm text-neutral-300 font-mono">
            {header}
          </div>
        )}

        <p className="text-xs text-neutral-400">{t("purchaseInvoices.attachmentsHelp")}</p>

        {items.length === 0 ? (
          <p className="text-sm text-neutral-600 italic">
            {t("purchaseInvoices.noAttachmentsYet")}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-2 rounded-md border border-neutral-800 px-3 py-2"
              >
                <button
                  onClick={() => openAttachment(a.id)}
                  className="text-left text-sm text-sky-300 hover:underline truncate"
                  title={a.originalFilename}
                >
                  📄 {a.originalFilename}
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {a.sizeBytes != null && (
                    <span className="text-[10px] text-neutral-600">
                      {Math.max(1, Math.round(a.sizeBytes / 1024))} KB
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => removeAttachment(a.id, a.originalFilename)}
                      className="text-xs text-red-400 hover:text-red-300"
                      title={t("purchaseInvoices.removeConfirm")}
                    >
                      {t("purchaseInvoices.removeBtn")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <label
          className={`block rounded-md border border-dashed px-4 py-3 text-center text-sm cursor-pointer transition-colors ${
            busy
              ? "border-neutral-800 text-neutral-600"
              : "border-neutral-700 text-neutral-300 hover:border-lime hover:text-lime"
          }`}
        >
          {busy ? t("purchaseInvoices.sending") : t("purchaseInvoices.attachFilesCta")}
          <input
            type="file"
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const fl = e.target.files;
              void handleUpload(fl);
              e.currentTarget.value = "";
            }}
          />
        </label>

        <div className="flex justify-end pt-2 border-t border-neutral-800">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("purchaseInvoices.close")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

type SupplierRow = {
  id: number;
  displayName: string;
  defaultTvaRate: string | null;
  defaultCategory: string | null;
};

function PiCreateModal({
  companyId,
  suppliers,
  onClose,
  onSaved,
}: {
  companyId: number;
  suppliers: ReadonlyArray<SupplierRow>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PiForm>({
    ...emptyForm,
    supplierId: suppliers[0]?.id ?? null,
    tvaRate: suppliers[0]?.defaultTvaRate ?? "20",
    category: (suppliers[0]?.defaultCategory ?? "autres") as Category,
  });
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const createMutation = trpc.purchaseInvoices.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  // F-dup-warning — aviso de possível duplicata (mesmo fornecedor + mesmo HT).
  const totalHtNum = Number(form.totalHt.replace(",", "."));
  const dupQuery = trpc.purchaseInvoices.possibleDuplicates.useQuery(
    {
      companyId,
      supplierId: form.supplierId ?? 0,
      totalHt: Number.isFinite(totalHtNum) ? totalHtNum : 0,
    },
    {
      enabled: form.supplierId != null && Number.isFinite(totalHtNum) && totalHtNum > 0,
    },
  );
  const dups = dupQuery.data ?? [];

  function handleSupplierChange(id: number) {
    const s = suppliers.find((x) => x.id === id);
    setForm((prev) => ({
      ...prev,
      supplierId: id,
      tvaRate: s?.defaultTvaRate ?? prev.tvaRate,
      category: (s?.defaultCategory ?? prev.category) as Category,
    }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.supplierId) {
      setError(t("purchaseInvoices.errSelectSupplier"));
      return;
    }
    const totalHt = Number(form.totalHt.replace(",", "."));
    const tvaRate = Number(form.tvaRate.replace(",", "."));
    if (!Number.isFinite(totalHt) || totalHt < 0) {
      setError(t("purchaseInvoices.errInvalidTotalHt"));
      return;
    }
    if (!Number.isFinite(tvaRate) || tvaRate < 0 || tvaRate > 30) {
      setError(t("purchaseInvoices.errInvalidTvaRate"));
      return;
    }
    const issueDate = form.issueDate || undefined;
    const dueDate = form.dueDate || undefined;
    const payload: {
      supplierId: number;
      invoiceNumberExternal: string;
      category: Category;
      totalHt: number;
      tvaRate: number;
      currency: string;
      issueDate?: string;
      dueDate?: string;
      notes?: string;
    } = {
      supplierId: form.supplierId,
      invoiceNumberExternal: form.invoiceNumberExternal.trim(),
      category: form.category,
      totalHt,
      tvaRate,
      currency: form.currency.toUpperCase(),
    };
    if (issueDate) payload.issueDate = issueDate;
    if (dueDate) payload.dueDate = dueDate;
    if (form.notes.trim()) payload.notes = form.notes.trim();
    createMutation.mutate({ companyId, data: payload });
  }

  return (
    <ModalShell title={t("purchaseInvoices.createModalTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("purchaseInvoices.colSupplier")} required>
            <select
              value={form.supplierId ?? ""}
              onChange={(e) => handleSupplierChange(Number(e.target.value))}
              required
              disabled={createMutation.isPending}
              className={inputClass}
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("purchaseInvoices.fieldInvoiceNumber")} required>
            <input
              value={form.invoiceNumberExternal}
              onChange={(e) => setForm({ ...form, invoiceNumberExternal: e.target.value })}
              required
              maxLength={64}
              disabled={createMutation.isPending}
              placeholder={t("purchaseInvoices.invoiceNumberPlaceholder")}
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("purchaseInvoices.colCategory")} required>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as Category })}
              disabled={createMutation.isPending}
              className={inputClass}
            >
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("purchaseInvoices.fieldCurrency")} required>
            <input
              value={form.currency}
              onChange={(e) =>
                setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })
              }
              maxLength={3}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
          <Field label={t("purchaseInvoices.fieldTotalHt")} required>
            <input
              type="text"
              inputMode="decimal"
              value={form.totalHt}
              onChange={(e) => setForm({ ...form, totalHt: e.target.value })}
              required
              disabled={createMutation.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
          <Field label={t("purchaseInvoices.fieldTvaRate")} required>
            <input
              type="text"
              inputMode="decimal"
              value={form.tvaRate}
              onChange={(e) => setForm({ ...form, tvaRate: e.target.value })}
              required
              disabled={createMutation.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
          <Field label={t("purchaseInvoices.fieldIssueDate")}>
            <input
              type="date"
              value={form.issueDate}
              onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
          <Field label={t("purchaseInvoices.fieldDueDate")}>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label={t("purchaseInvoices.fieldNotes")}>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={4000}
            rows={2}
            disabled={createMutation.isPending}
            className={`${inputClass} resize-y`}
          />
        </Field>

        {/* Preview do cálculo */}
        <div className="bg-neutral-800/30 rounded-md px-3 py-2 text-sm flex justify-between font-mono">
          <span className="text-neutral-400">
            {t("purchaseInvoices.previewLabel")}: HT {form.totalHt} × {form.tvaRate}% =
          </span>
          <span className="text-lime">
            TVA{" "}
            {(
              (Number(form.totalHt.replace(",", ".")) || 0) *
              ((Number(form.tvaRate.replace(",", ".")) || 0) / 100)
            ).toFixed(2)}{" "}
            € · TTC{" "}
            {(
              (Number(form.totalHt.replace(",", ".")) || 0) *
              (1 + (Number(form.tvaRate.replace(",", ".")) || 0) / 100)
            ).toFixed(2)}{" "}
            €
          </span>
        </div>

        {dups.length > 0 && (
          <div className="text-amber-200 text-sm bg-amber-500/10 border border-amber-500/40 rounded-md px-3 py-2">
            <div className="font-medium text-amber-300">{t("purchaseInvoices.dupWarnTitle")}</div>
            <ul className="mt-1 space-y-0.5">
              {dups.map((d) => (
                <li key={d.id} className="tabular-nums">
                  {t("purchaseInvoices.dupNumberPrefix")} <b>{d.invoiceNumberExternal}</b> ·{" "}
                  {Number(d.totalTtc).toFixed(2)} € · {d.status}
                  {d.issueDate ? ` · ${d.issueDate}` : ""}
                </li>
              ))}
            </ul>
            <div className="mt-1 text-amber-200/70">{t("purchaseInvoices.dupWarnHint")}</div>
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("purchaseInvoices.cancelFormBtn")}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending
              ? t("purchaseInvoices.creating")
              : t("purchaseInvoices.createBtn")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-3xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
